import { useForm } from '@mantine/form';

import { useTranslation } from 'react-i18next';

import { useCreateResponseProjectMutation } from '../../../hooks/useCreateResponseProject.hooks';

interface CreateOrEditResponseProjectFormValues {
  tender?: {
    id: number;
    title: string;
    buyerId: string;
    buyerName: string;
    responseDeadline?: Date;
  };
  responseDeadline?: Date;
  properties: number[];
}

export function useCreateOrEditResponseProjectForm({
  closePanel,
}: {
  closePanel: () => void;
}) {
  const { t } = useTranslation('bidResponse');
  const { mutate: createResponseProject, isPending } =
    useCreateResponseProjectMutation({
      onSuccess: closePanel,
    });

  const form = useForm<CreateOrEditResponseProjectFormValues>({
    validateInputOnChange: true,
    validate: {
      responseDeadline: value =>
        value
          ? null
          : t('project.createOrEditProject.responseDeadlineField.error'),
      tender: value =>
        value?.id
          ? null
          : t('project.createOrEditProject.selectTenderField.error'),
    },
    initialValues: {
      tender: undefined,
      properties: [],
      responseDeadline: undefined,
    },
  });

  function updateTender(
    tender: CreateOrEditResponseProjectFormValues['tender'] | undefined,
  ) {
    form.setFieldValue('tender', tender);
    if (tender?.responseDeadline) {
      form.setFieldValue('responseDeadline', tender.responseDeadline);
    }
  }

  function updateProperties(
    properties: CreateOrEditResponseProjectFormValues['properties'],
  ) {
    form.setFieldValue('properties', properties);
  }

  function handleSubmit(values: CreateOrEditResponseProjectFormValues) {
    if (!values.tender || !values.responseDeadline) {
      return;
    }

    createResponseProject({
      title: values.tender.title,
      buyerName: values.tender.buyerName,
      responseDeadline: values.responseDeadline,
      tenderId: values.tender.id,
      filters: {
        optionIds: values.properties,
      },
    });
  }

  const tender = {
    value: form.values.tender,
    update: updateTender,
    error: form.errors.tender,
  };

  const properties = {
    value: form.values.properties,
    update: updateProperties,
    error: form.errors.properties,
  };

  return {
    form,
    tender,
    properties,
    onSubmit: form.onSubmit(handleSubmit),
    isLoading: isPending,
  };
}
